/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { PropertySettings } from 'src/components/Property';
import PropertySettingsPlaceholder from 'src/components/Property/PropertySettings/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'property.property_settings.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the property settings. Please try again.',
  },
});

export const propertySettingsRouteQuery = graphql`
  query PropertySettingsRoute_Query($uuid: ID!) {
    property(uuid: $uuid) {
      ...PropertySettings_property
    }
  }
`;

const propertySettingsRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `PropertySettings` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} property settings route content.
 */
function PropertySettingsPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(propertySettingsRouteQuery, queryRef);
  return <PropertySettings {...routeProps} {...queryData} />;
}

PropertySettingsPreloadedQueryRenderer.propTypes = {
  routeProps: propertySettingsRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `PropertySettings` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} property settings route content.
 */
function PropertySettingsRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    propertySettingsRouteQuery,
    routeProps,
    { logLabel: 'PropertySettingsRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={PropertySettingsPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <PropertySettingsPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <PropertySettings {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PropertySettingsRouteRenderer.propTypes = {
  routeProps: propertySettingsRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the PropertySettings route state supplied by found-relay.
 * Network errors are forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch them before pending props resolve.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} PropertySettings route content or loading state.
 */
export function propertySettingsRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <PropertySettingsRouteRenderer
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return <PropertySettingsRouteRenderer routeProps={props} />;
}

export default PropertySettingsRouteRenderer;
